import { Dialog, PercentageField, Stack, ToggleSwitch } from "@tm/components"
import { useEffect, useMemo, useState } from "react"
import { useGetFormValidationError, useLocalization } from "@tm/localization"
import { PriceType } from "@tm/models"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ensureNumber, errorHandling, requiredNumber } from "@tm/utils"
import { PartBasicInfo } from "../../../../../PartDialog/PartBasicInfo"
import { EditDialogHeader } from "../../../../../PartDialog/EditDialogHeader"
import { ValueLabelStack } from "../../../../../../ValueLabelStack"
import { CustomStyledAmountField } from "../../../../../../StyledComponents"
import { findPrice } from "../../../../../../../helpers"
import { CustomOrderPriceCalculation } from "../../../../../../../data/model"
import { BasketPart } from "../../../../../../../models"

type Props = {
    part: BasketPart
    hidePurchasePrice?: boolean
    disableHeaderButtons?: boolean
    onCloseButtonClick(): void
    onEditOrderPrice?(part: BasketPart, customOrderPriceCalculation: CustomOrderPriceCalculation): void
}

export function EditDialog({ part, hidePurchasePrice, disableHeaderButtons, onEditOrderPrice, onCloseButtonClick }: Props) {
    const { translateText } = useLocalization()
    const getError = useGetFormValidationError()

    const { erpInfoResponse, partItem } = part
    const { customOrderPriceCalculation } = partItem.orderItem || {}
    const erpPrices = erpInfoResponse?.prices ? Object.values(erpInfoResponse?.prices) : undefined
    const purchasePrice = findPrice(erpPrices, PriceType.Purchase)
    const minimumPurchasePrice = findPrice(erpPrices, PriceType.ValidationPurchaseMinPrice)?.value

    const [defaultValues, currencyCode, currencySymbol] = useMemo(() => {
        return [
            {
                fixedPrice: customOrderPriceCalculation?.amount ?? purchasePrice?.value ?? 0,
                rebate: customOrderPriceCalculation?.rebate ?? 0,
                calculatedPrice: 0,
            },
            customOrderPriceCalculation?.currencyCode_Iso_4217 ?? purchasePrice?.currencyCode_Iso_4217,
            customOrderPriceCalculation?.currencySymbol ?? purchasePrice?.currencySymbol,
        ]
    }, [customOrderPriceCalculation, purchasePrice])

    const [hasFixedPrice, setHasFixedPrice] = useState<boolean>(!defaultValues.rebate)

    const schema = useMemo(() => {
        if (hasFixedPrice) {
            return z.object({
                fixedPrice: ensureNumber(z.number().min(minimumPurchasePrice ?? 0, getError("PRICE_TOO_LOW"))),
            })
        }
        return z
            .object({
                rebate: requiredNumber(),
                calculatedPrice: z.number(),
            })
            .refine(
                (x) => {
                    if (!purchasePrice || !minimumPurchasePrice) {
                        return false
                    }
                    return x.calculatedPrice >= minimumPurchasePrice
                },
                { message: getError("PRICE_TOO_LOW"), path: ["rebate"] }
            )
    }, [hasFixedPrice, purchasePrice, minimumPurchasePrice])

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit, clearErrors, watch, setValue, control } = form

    const actualRebate = Number(watch("rebate"))
    const actualFixedPrice = Number(watch("fixedPrice"))
    const calculatedPrice = Number(watch("calculatedPrice"))

    useEffect(() => {
        let calculated = 0
        if (purchasePrice) {
            if (!hasFixedPrice) {
                calculated = purchasePrice.value - purchasePrice.value * (actualRebate / 100)
            } else {
                calculated = actualFixedPrice
            }
        }
        setValue("calculatedPrice", calculated)
    }, [purchasePrice, actualRebate, actualFixedPrice, hasFixedPrice])

    useEffect(() => {
        setHasFixedPrice(!customOrderPriceCalculation?.rebate)
    }, [customOrderPriceCalculation?.rebate])

    function handleConfirmButtonClick(data: Record<string, unknown>) {
        const { fixedPrice, rebate } = data
        onEditOrderPrice?.(part, {
            priceType: PriceType.Purchase,
            amount: fixedPrice as number,
            rebate: rebate as number,
            currencySymbol,
            currencyCode_Iso_4217: currencyCode,
        })
        onCloseButtonClick()
    }

    function handleToggleSwitchChange() {
        setHasFixedPrice(!hasFixedPrice)
    }

    useEffect(() => {
        clearErrors()
    }, [hasFixedPrice])

    return (
        <Dialog open position="middle" fullWidth maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <Stack gap={2}>
                        <EditDialogHeader isSaving={disableHeaderButtons} onCloseButtonClick={onCloseButtonClick} />
                        <Stack gap={2}>
                            <PartBasicInfo
                                part={part}
                                hidePurchasePrice={hidePurchasePrice}
                                showManufacturer
                                showSupplierArticleNumbers
                                showWholesalerArticleNumbers
                            />
                            <Stack direction="row" flex={1} gap={3} alignSelf="flex-end" alignItems="center">
                                <ToggleSwitch
                                    size="small"
                                    right={{ value: translateText(54), key: true }}
                                    left={{ value: translateText(246), key: true }}
                                    selected={!hasFixedPrice}
                                    onChange={handleToggleSwitchChange}
                                />
                                {hasFixedPrice ? (
                                    <Controller
                                        control={control}
                                        key="fixedPrice"
                                        name="fixedPrice"
                                        render={({ field, fieldState }) => (
                                            <CustomStyledAmountField
                                                label={`${translateText(55)} ${currencyCode}`}
                                                min={0}
                                                max={99999.99}
                                                step={0.01}
                                                required
                                                {...field}
                                                {...errorHandling(fieldState)}
                                            />
                                        )}
                                    />
                                ) : (
                                    <Controller
                                        control={control}
                                        key="rebate"
                                        name="rebate"
                                        render={({ field, fieldState }) => (
                                            <PercentageField label={`${translateText(54)} %`} required {...field} {...errorHandling(fieldState)} />
                                        )}
                                    />
                                )}
                                <ValueLabelStack label={`${translateText(57)} ${currencyCode}`} value={calculatedPrice} />
                            </Stack>
                        </Stack>
                    </Stack>
                </form>
            </FormProvider>
        </Dialog>
    )
}
